// NOTE: Modal container should use max-w-6xl for better width.
import React, { useState, useEffect } from "react";
import { AccommodationSystem } from "../../../../types/optionalSystems";
import { getSystemTonnageFromState } from "../../../../store/selectors/shipBuilderSelectors";
import { useAppSelector } from "../../../../store";
import { selectors } from "../../../../store/selectors";

interface MESInlineEditorProps {
  availableSystems: AccommodationSystem[];
  shipBuilderState: any;
  onConfirm: (updatedSystem: AccommodationSystem) => void;
  onCancel: () => void;
  editingSystem?: AccommodationSystem | null;
}

const ALL_SHIP_SPACES = [
  { id: "bridge", label: "Bridge" },
  { id: "computer", label: "Computer" },
  { id: "sensors", label: "Sensors" },
  { id: "powerPlant", label: "Power Plant" },
  { id: "maneuverDrive", label: "Maneuver Drive" },
  { id: "jumpDrive", label: "Jump Drive" },
  { id: "reactionDrive", label: "Reaction Drive" },
  { id: "fuelTanks", label: "Fuel Tanks" },
  { id: "staterooms", label: "Staterooms" },
  { id: "cargo", label: "Cargo" },
  { id: "weapons", label: "Weapons" },
  { id: "hangar", label: "Hangar Bay" },
  { id: "medicalBay", label: "Medical Bay" },
  { id: "droneBay", label: "Drone Bay" },
  { id: "externalSystems", label: "External Systems" },
  { id: "internalSystems", label: "Internal Systems" },
  { id: "modularHull", label: "Modular Hull" },
];

const MESInlineEditor: React.FC<MESInlineEditorProps> = ({
  availableSystems,
  shipBuilderState,
  onConfirm,
  onCancel,
  editingSystem,
}) => {
  const [multiEnvSelections, setMultiEnvSelections] = useState<{
    [id: string]: { selected: boolean; tons: number; environmentType: string };
  }>({});
  const [customSelected, setCustomSelected] = useState(false);
  const [customTons, setCustomTons] = useState(0);
  const [customEnvType, setCustomEnvType] = useState("");

  // Pre-fill selections when editing
  useEffect(() => {
    if (editingSystem && editingSystem.coveredSpaces) {
      const selections: {
        [id: string]: {
          selected: boolean;
          tons: number;
          environmentType: string;
        };
      } = {};
      editingSystem.coveredSpaces.forEach((space) => {
        if (space.id === "custom") {
          setCustomSelected(true);
          setCustomTons(space.tons);
          setCustomEnvType(space.environmentType);
        } else {
          selections[space.id] = {
            selected: true,
            tons: space.tons,
            environmentType: space.environmentType,
          };
        }
      });
      setMultiEnvSelections(selections);
    } else {
      setMultiEnvSelections({});
      setCustomSelected(false);
      setCustomTons(0);
      setCustomEnvType("");
    }
  }, [editingSystem]);

  const hasValidArea = Object.entries(multiEnvSelections).some(
    ([id, v]) =>
      v.selected &&
      typeof v.environmentType === "string" &&
      v.environmentType.trim() !== "" &&
      Number(v.tons) > 0,
  );
  const hasValidCustom =
    customSelected && customEnvType.trim() !== "" && Number(customTons) > 0;

  // Prepare covered spaces for selector
  const coveredSpaces = [
    ...Object.entries(multiEnvSelections)
      .filter(
        ([id, v]) => v.selected && v.environmentType && Number(v.tons) > 0,
      )
      .map(([id, v]) => ({ tons: Number(v.tons) })),
    ...(hasValidCustom ? [{ tons: Number(customTons) }] : []),
  ];

  // Use selector for calculations
  const mesStats = useAppSelector((state) =>
    selectors.derived.selectMESInlineEditorStats(state, coveredSpaces),
  );

  const handleConfirm = () => {
    const base = availableSystems.find(
      (s) => s.subtype === "multiEnvironmentSpace",
    );
    if (!base) return;
    // Gather all selected areas
    const coveredSpacesData = [
      ...Object.entries(multiEnvSelections)
        .filter(
          ([id, v]) => v.selected && v.environmentType && Number(v.tons) > 0,
        )
        .map(([id, v]) => {
          const area = ALL_SHIP_SPACES.find((s) => s.id === id);
          return {
            id,
            label: area ? area.label : id,
            tons: Number(v.tons),
            environmentType: v.environmentType,
          };
        }),
      ...(hasValidCustom
        ? [
            {
              id: "custom",
              label: "Custom Area",
              tons: Number(customTons),
              environmentType: customEnvType,
            },
          ]
        : []),
    ];

    const updatedSystem: AccommodationSystem = {
      ...(editingSystem || base),
      id: editingSystem?.id || `multiEnv-${Date.now()}`,
      tonnage: mesStats.tonnage,
      cost: mesStats.cost,
      power: mesStats.power,
      properties: {
        ...base.properties,
        tonnage: mesStats.tonnage,
        equipmentTonnage: mesStats.equipmentTonnage,
      },
      coveredSpaces: coveredSpacesData,
    };
    // Debug output
    console.log("MES DEBUG: coveredSpaces", coveredSpacesData);
    console.log("MES DEBUG: updatedSystem", updatedSystem);
    console.log("MES DEBUG: calling onConfirm with system:", updatedSystem);
    onConfirm(updatedSystem);
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h4 className="font-medium text-gray-900 mb-3">
        {editingSystem
          ? "Edit Multi-Environment Space"
          : "Add Multi-Environment Space"}
      </h4>

      {/* Custom Area - Designated Area */}
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            checked={customSelected}
            onChange={(e) => setCustomSelected(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-sm font-semibold text-blue-800 bg-blue-100 px-2 py-1 rounded">
            DESIGNATED AREA
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-blue-800 font-medium">
            Size in Tons:
          </label>
          <input
            type="number"
            min={0}
            step={0.1}
            value={customTons}
            onChange={(e) => setCustomTons(Number(e.target.value))}
            className="w-24 px-2 py-1 border rounded bg-white"
            placeholder="Tons"
            disabled={!customSelected}
          />
          <input
            type="text"
            placeholder="Environment Type"
            value={customEnvType}
            onChange={(e) => setCustomEnvType(e.target.value)}
            className="w-48 px-2 py-1 border rounded bg-white"
            disabled={!customSelected}
          />
        </div>
      </div>

      {/* Ship Areas Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {ALL_SHIP_SPACES.map((space) => {
          const maxTons = getSystemTonnageFromState(shipBuilderState, space.id);
          const sel = multiEnvSelections[space.id] || {
            selected: false,
            tons: maxTons,
            environmentType: "",
          };
          return (
            <div
              key={space.id}
              className="flex items-center gap-2 border-b py-1"
            >
              <input
                type="checkbox"
                checked={sel.selected}
                disabled={maxTons === 0}
                onChange={(e) =>
                  setMultiEnvSelections((s) => ({
                    ...s,
                    [space.id]: {
                      ...sel,
                      selected: e.target.checked,
                      tons: maxTons,
                    },
                  }))
                }
              />
              <span className="w-32">
                {space.label}{" "}
                <span className="text-xs text-gray-500">({maxTons} tons)</span>
              </span>
              {sel.selected && (
                <>
                  <span className="w-24 px-2 py-1 border rounded bg-gray-100 text-gray-600 text-center">
                    {maxTons}
                  </span>
                  <input
                    type="text"
                    placeholder="Environment Type"
                    value={sel.environmentType}
                    onChange={(e) =>
                      setMultiEnvSelections((s) => ({
                        ...s,
                        [space.id]: { ...sel, environmentType: e.target.value },
                      }))
                    }
                    className="w-32 px-2 py-1 border rounded"
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* Live Summary */}
      <div className="mb-4 p-2 bg-gray-100 rounded text-sm">
        <b>Summary:</b> Total Space: {mesStats.totalTons} tons | Equipment: +
        {mesStats.equipmentTonnage} tons | Cost:{" "}
        {mesStats.cost.toLocaleString()} Cr | Power: {mesStats.power}
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <button
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!hasValidArea && !hasValidCustom}
          onClick={handleConfirm}
        >
          {editingSystem ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
};

export default MESInlineEditor;
